import React, { useState } from "react";
import { PayPalButtons, PayPalScriptProvider } from "@paypal/react-paypal-js";
import { toast } from "react-hot-toast";
import { useTranslation } from "react-i18next";

import { useOrder } from "@/app/providers";
import { useNavigation } from "@/shared/hooks/useNavigation";
import { useOrderPersonalization } from "@/shared/hooks/useOrderPersonalization";
import { createPayPalOrder, capturePayPalOrder } from "@/services/payment/paypal-service";

interface PayPalPaymentFormProps {
    clientId: string;
    organizationId: string;
    amount: number; // En euros
    isExtension?: boolean;
}

const PayPalPaymentForm: React.FC<PayPalPaymentFormProps> = ({
    clientId,
    organizationId,
    amount,
    isExtension = false,
}) => {
    const { t } = useTranslation();
    const { orderData, setIsOrderComplete, currentOrderId, isMergedOrder } = useOrder();
    const { navigateToConfirmation } = useNavigation();
    const personalization = useOrderPersonalization();
    const [isProcessing, setIsProcessing] = useState(false);

    // Convertir le montant en centimes pour notre backend
    const amountInCents = Math.round(amount * 100);

    const handleCreateOrder = async () => {
        try {
            setIsProcessing(true);

            let productIndex = 0;
            if (isMergedOrder && orderData.products && orderData.products.length > 0) {
                productIndex = orderData.products.length - 1;
            }

            const response = await createPayPalOrder({
                organizationId,
                amount: amountInCents,
                currency: "EUR",
                customerEmail: orderData.email || "",
                customerName: `${orderData.firstName || ""} ${orderData.lastName || ""}`.trim(),
                description: `Paiement pour commande ${currentOrderId}`,
                metadata: {
                    orderId: currentOrderId,
                    productIndex,
                    productsAmount: (amountInCents - (isMergedOrder ? 0 : (orderData.shippingCost || 0) * 100)).toString(),
                    shippingCost: (isMergedOrder ? 0 : (orderData.shippingCost || 0) * 100).toString(),
                },
            });

            return response.clientSecret; // L'ID d'ordre PayPal
        } catch (error: any) {
            console.error("Error creating PayPal order:", error);
            toast.error(t("payment.errors.intentCreation"));
            throw error;
        } finally {
            setIsProcessing(false);
        }
    };

    const handleApprove = async (data: any) => {
        try {
            setIsProcessing(true);
            console.log("✅ PayPal order approved, capturing...", data.orderID);

            await capturePayPalOrder(data.orderID, organizationId);

            toast.success(t("payment.success.message"));
            setIsOrderComplete(true);
            navigateToConfirmation();
        } catch (error: any) {
            console.error("Error capturing PayPal order:", error);
            toast.error(t("payment.errors.payment", { message: error.message }));
        } finally {
            setIsProcessing(false);
        }
    };

    return (
        <div className="space-y-4">
            <div className="relative z-0">
                <PayPalScriptProvider
                    options={{
                        clientId: clientId,
                        currency: "EUR",
                        intent: "capture",
                        components: "buttons",
                        "disable-funding": isExtension ? "card" : undefined,
                    }}
                >
                    <PayPalButtons
                        style={{
                            layout: "vertical",
                            color: (personalization.button.primary.includes('blue') || personalization.button.primary.includes('0000FF')) ? "blue" : "gold",
                            shape: personalization.button.borderRadius?.includes('full') ? "pill" : "rect",
                            label: "pay",
                            tagline: false
                        }}
                        disabled={isProcessing}
                        createOrder={handleCreateOrder}
                        onApprove={handleApprove}
                        onError={(err) => {
                            console.error("PayPal Error:", err);
                            toast.error("Une erreur est survenue avec PayPal");
                        }}
                    />
                </PayPalScriptProvider>

                {isProcessing && (
                    <div className="absolute inset-0 bg-white/50 flex items-center justify-center z-10 rounded-md">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default PayPalPaymentForm;
